import React, { useEffect, useMemo, useRef, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  CardContent,
  Divider,
  Avatar,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  DirectionsCar as CarIcon,
  Today as TodayIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ReservationStatusChip } from '../components/ReservationStatusChip';
import { vehicleApi, reservationApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Vehicle, VehicleType, VehicleClass } from '../types/vehicle';
import { Reservation, ReservationStatus } from '../types/reservation';
import { APP_DATE_FORMAT, formatDate } from '../utils/formatters';
import { getReservationStatusColor } from '../utils/reservationStatusStyles';
import { vehicleTypeLabel, vehicleClassLabel } from '../i18n/translate';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getCompanyRoutePath } from '../navigation/companyNavigation';

const SIDEBAR_WIDTH = 248;
const ROW_HEIGHT = 52;
const ZOOM_LEVELS = [128, 104, 84, 64, 48, 36, 26] as const;
const DEFAULT_ZOOM_INDEX = ZOOM_LEVELS.length - 1;
const OVERSCAN_DAYS = 3;
const MIN_VISIBLE_DAYS = 7;
const DEFAULT_VIEWPORT_WIDTH = ZOOM_LEVELS[DEFAULT_ZOOM_INDEX] * 33;
const FETCH_PAD_DAYS = 45;
const FETCH_MARGIN_DAYS = 20;
const DRAG_THRESHOLD_PX = 4;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const getDefaultViewStart = () => dayjs().subtract(1, 'day').startOf('day');

interface TimelineRentalDraft {
  vehicle: Vehicle;
  startDate: Dayjs;
  endDate: Dayjs;
}

interface TimelinePendingSelection {
  vehicleId: number;
  startDate: Dayjs;
}

export const VehicleTimelinePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = user?.company?.id;

  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [selectedRentalDraft, setSelectedRentalDraft] = useState<TimelineRentalDraft | null>(null);
  const [pendingSelection, setPendingSelection] = useState<TimelinePendingSelection | null>(null);
  const [viewStart, setViewStart] = useState<Dayjs>(getDefaultViewStart);
  const [filterType, setFilterType] = useState<VehicleType | ''>('');
  const [filterClass, setFilterClass] = useState<VehicleClass | ''>('');
  const [searchQuery, setSearchQuery] = useState('');

  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  const pixelsPerDay = ZOOM_LEVELS[zoomIndex];
  const canZoomIn = zoomIndex > 0;
  const canZoomOut = zoomIndex < ZOOM_LEVELS.length - 1;
  const useStackedDayLabel = pixelsPerDay < 36;
  const dayLabelFormat = useStackedDayLabel ? 'D/M' : pixelsPerDay < 50 ? 'D MMM' : 'ddd D';

  const [viewportWidth, setViewportWidth] = useState(0);
  const timelineViewportRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragRemainderPx, setDragRemainderPx] = useState(0);
  const dragStateRef = useRef<{
    active: boolean;
    startX: number;
    originViewStart: Dayjs;
    pressedVehicleId: number | null;
    pressedDay: string | null;
    pressedReservationId: string | null;
  }>({
    active: false,
    startX: 0,
    originViewStart: viewStart,
    pressedVehicleId: null,
    pressedDay: null,
    pressedReservationId: null,
  });
  const dragMovedRef = useRef(false);

  useEffect(() => {
    const el = timelineViewportRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setViewportWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const visibleDays = Math.max(
    MIN_VISIBLE_DAYS,
    Math.ceil((viewportWidth || DEFAULT_VIEWPORT_WIDTH) / pixelsPerDay)
  );
  const totalCanvasDays = visibleDays + OVERSCAN_DAYS * 2;

  const canvasStart = useMemo(() => viewStart.subtract(OVERSCAN_DAYS, 'day'), [viewStart]);
  const canvasEnd = useMemo(
    () => viewStart.add(visibleDays + OVERSCAN_DAYS, 'day'),
    [viewStart, visibleDays]
  );
  const canvasDays = useMemo(
    () => Array.from({ length: totalCanvasDays }, (_, index) => canvasStart.add(index, 'day').startOf('day')),
    [canvasStart, totalCanvasDays]
  );

  const [fetchWindow, setFetchWindow] = useState(() => ({
    start: canvasStart.subtract(FETCH_PAD_DAYS, 'day'),
    end: canvasEnd.add(FETCH_PAD_DAYS, 'day'),
  }));

  useEffect(() => {
    setFetchWindow((prev) => {
      let { start, end } = prev;
      let changed = false;
      if (canvasStart.diff(start, 'day') < FETCH_MARGIN_DAYS) {
        start = canvasStart.subtract(FETCH_PAD_DAYS, 'day');
        changed = true;
      }
      if (end.diff(canvasEnd, 'day') < FETCH_MARGIN_DAYS) {
        end = canvasEnd.add(FETCH_PAD_DAYS, 'day');
        changed = true;
      }
      return changed ? { start, end } : prev;
    });
  }, [canvasStart, canvasEnd]);

  const fetchStartKey = fetchWindow.start.format('YYYY-MM-DD');
  const fetchEndKey = fetchWindow.end.format('YYYY-MM-DD');

  const { data: vehicles = [], isLoading: vehiclesLoading, refetch: refetchVehicles } = useQuery(
    ['companyVehicles', companyId],
    () => vehicleApi.getByCompany(String(companyId!)),
    { enabled: !!companyId }
  );

  const { data: reservations = [], isLoading: reservationsLoading, refetch: refetchReservations } = useQuery(
    ['companyReservationsRange', companyId, fetchStartKey, fetchEndKey],
    () => reservationApi.getCompanyReservationsInRange(companyId!, fetchStartKey, fetchEndKey),
    { enabled: !!companyId, keepPreviousData: true }
  );

  const handleRefresh = () => {
    setViewStart(getDefaultViewStart());
    setSelectedReservation(null);
    setSelectedRentalDraft(null);
    setPendingSelection(null);
    refetchVehicles();
    refetchReservations();
  };

  const handleJumpToToday = () => setViewStart(getDefaultViewStart());

  const adjustZoom = (direction: 'in' | 'out') => {
    setZoomIndex((current) => {
      if (direction === 'in') return Math.max(0, current - 1);
      return Math.min(ZOOM_LEVELS.length - 1, current + 1);
    });
  };

  // Rentals are billed inclusive of both the pickup and return day (see calculateInclusiveRentalDays
  // on the backend), so a reservation occupies the full return day too - not just up to its start.
  const getReservationReturnBoundary = (reservation: Reservation): Dayjs | null => {
    const returnDateValue = reservation.returnDate || reservation.endDate;
    return returnDateValue ? dayjs(returnDateValue).add(1, 'day').startOf('day') : null;
  };

  const reservationsInRange = useMemo(() => {
    const start = canvasStart.toDate();
    const end = canvasEnd.toDate();
    return reservations.filter((r) => {
      const pickup = new Date(r.pickupDate || r.startDate);
      const returnBoundary = getReservationReturnBoundary(r);
      const returnD = returnBoundary ? returnBoundary.toDate() : end;
      return pickup <= end &&
        returnD >= start &&
        r.status !== ReservationStatus.CANCELLED &&
        r.status !== ReservationStatus.DECLINED;
    });
  }, [reservations, canvasStart, canvasEnd]);

  const reservationsByVehicle = useMemo(() => {
    const map = new Map<number, Reservation[]>();
    reservationsInRange.forEach((r) => {
      const vid = r.vehicle?.id;
      if (vid) {
        if (!map.has(vid)) map.set(vid, []);
        map.get(vid)!.push(r);
      }
    });
    return map;
  }, [reservationsInRange]);

  const getVehicleSearchLabel = (vehicle: Vehicle) =>
    `${vehicle.make} ${vehicle.model}${vehicle.licensePlate ? ` • ${vehicle.licensePlate}` : ''}`;

  const vehicleSearchOptions = useMemo(
    () =>
      [...vehicles].sort((a, b) => getVehicleSearchLabel(a).localeCompare(getVehicleSearchLabel(b))),
    [vehicles]
  );

  const displayedVehicles = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return vehicles
      .filter((v) => !filterType || v.type === filterType)
      .filter((v) => !filterClass || v.vehicleClass === filterClass)
      .filter(
        (v) =>
          !query ||
          v.make.toLowerCase().includes(query) ||
          v.model.toLowerCase().includes(query) ||
          (v.licensePlate || '').toLowerCase().includes(query)
      )
      .sort((a, b) => {
        const aLabel = a.licensePlate || `${a.make} ${a.model}`;
        const bLabel = b.licensePlate || `${b.make} ${b.model}`;
        return aLabel.localeCompare(bLabel);
      });
  }, [vehicles, filterType, filterClass, searchQuery]);

  const hasActiveFilters = Boolean(filterType || filterClass || searchQuery);

  const handleClearFilters = () => {
    setFilterType('');
    setFilterClass('');
    setSearchQuery('');
  };

  const getVehicleLabel = (vehicle: Vehicle) => vehicle.licensePlate || `${vehicle.make} ${vehicle.model}`;

  const doesReservationOccupyDay = (reservation: Reservation, day: Dayjs) => {
    const dayStart = day.startOf('day');
    const dayEnd = dayStart.add(1, 'day');
    const pickup = dayjs(reservation.pickupDate || reservation.startDate);
    const returnBoundary = getReservationReturnBoundary(reservation) ?? dayEnd;
    return pickup.isBefore(dayEnd) && returnBoundary.isAfter(dayStart);
  };

  const isVehicleDayAvailable = (vehicleId: number, day: Dayjs) =>
    !(reservationsByVehicle.get(vehicleId) ?? []).some((reservation) => doesReservationOccupyDay(reservation, day));

  const isVehicleRangeAvailable = (vehicleId: number, rangeStart: Dayjs, rangeEnd: Dayjs) => {
    let cursor = rangeStart.startOf('day');
    while (!cursor.isAfter(rangeEnd, 'day')) {
      if (!isVehicleDayAvailable(vehicleId, cursor)) {
        return false;
      }
      cursor = cursor.add(1, 'day');
    }
    return true;
  };

  const getReservationPosition = (res: Reservation) => {
    const canvasStartMs = canvasStart.toDate().getTime();
    const pickup = new Date(res.pickupDate || res.startDate).getTime();
    const returnBoundary = getReservationReturnBoundary(res);
    const returnD = (returnBoundary ? returnBoundary.toDate() : canvasEnd.toDate()).getTime();
    const left = Math.max(0, ((pickup - canvasStartMs) / DAY_IN_MS) * pixelsPerDay);
    // Derive width from the right edge (relative to canvas start) rather than the raw
    // pickup-to-return duration, so clipping the left edge to 0 (when pickup is before the
    // rendered window) correspondingly shortens the bar instead of pushing its end too far right.
    const rightEdge = Math.min(
      ((returnD - canvasStartMs) / DAY_IN_MS) * pixelsPerDay,
      totalCanvasDays * pixelsPerDay
    );
    const width = Math.max(rightEdge - left, 0) || pixelsPerDay;
    return { left, width };
  };

  const isLoading = vehiclesLoading || reservationsLoading;
  const showSidePanel = Boolean(selectedReservation || selectedRentalDraft);
  const selectedRangeDays = selectedRentalDraft
    ? Math.max(selectedRentalDraft.endDate.diff(selectedRentalDraft.startDate, 'day'), 1)
    : 0;
  const today = dayjs();

  const handleTimelinePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const target = event.target as HTMLElement;
    const dayCell = target.closest<HTMLElement>('[data-role="day-cell"]');
    const reservationEl = target.closest<HTMLElement>('[data-role="reservation"]');
    dragStateRef.current = {
      active: true,
      startX: event.clientX,
      originViewStart: viewStart,
      pressedVehicleId: dayCell ? Number(dayCell.dataset.vehicleId) : null,
      pressedDay: dayCell ? dayCell.dataset.day ?? null : null,
      pressedReservationId: reservationEl ? reservationEl.dataset.reservationId ?? null : null,
    };
    dragMovedRef.current = false;
    setIsDragging(true);
    // Pointer capture is what lets the drag keep tracking outside the timeline bounds; since
    // it retargets subsequent pointer events to this container, click resolution for day cells
    // and reservation bars is done manually below from the data captured above, not via onClick.
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleTimelinePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragStateRef.current;
    if (!drag.active) return;
    const dx = event.clientX - drag.startX;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX) {
      dragMovedRef.current = true;
    }
    const daysShifted = Math.round(dx / pixelsPerDay);
    const newViewStart = drag.originViewStart.subtract(daysShifted, 'day');
    setViewStart((current) => (newViewStart.isSame(current, 'day') ? current : newViewStart));
    setDragRemainderPx(dx - daysShifted * pixelsPerDay);
  };

  const handleTimelinePointerEnd = () => {
    if (!dragStateRef.current.active) return;
    const { pressedVehicleId, pressedDay, pressedReservationId } = dragStateRef.current;
    dragStateRef.current.active = false;
    setIsDragging(false);
    setDragRemainderPx(0);

    if (dragMovedRef.current) return;

    if (pressedReservationId != null) {
      const res = reservationsInRange.find((r) => String(r.id) === pressedReservationId);
      if (res) {
        setSelectedRentalDraft(null);
        setPendingSelection(null);
        setSelectedReservation(res);
      }
      return;
    }

    if (pressedVehicleId != null && pressedDay) {
      const vehicle = displayedVehicles.find((v) => v.id === pressedVehicleId);
      if (vehicle) {
        handleTimelineDayClick(vehicle, dayjs(pressedDay));
      }
    }
  };

  const handleTimelineDayClick = (vehicle: Vehicle, day: Dayjs) => {
    const selectedDay = day.startOf('day');
    setSelectedReservation(null);

    if (!pendingSelection || pendingSelection.vehicleId !== vehicle.id) {
      setSelectedRentalDraft(null);
      setPendingSelection({ vehicleId: vehicle.id, startDate: selectedDay });
      return;
    }

    if (!selectedDay.isAfter(pendingSelection.startDate, 'day')) {
      setSelectedRentalDraft(null);
      setPendingSelection({ vehicleId: vehicle.id, startDate: selectedDay });
      return;
    }

    if (!isVehicleRangeAvailable(vehicle.id, pendingSelection.startDate, selectedDay)) {
      setSelectedRentalDraft(null);
      setPendingSelection({ vehicleId: vehicle.id, startDate: selectedDay });
      return;
    }

    setSelectedRentalDraft({
      vehicle,
      startDate: pendingSelection.startDate,
      endDate: selectedDay,
    });
    setPendingSelection(null);
  };

  const handleCreateRental = () => {
    if (!selectedRentalDraft) {
      return;
    }

    navigate(getCompanyRoutePath('rentals'), {
      state: {
        rentalPrefill: {
          vehicle: selectedRentalDraft.vehicle,
          startDate: selectedRentalDraft.startDate.format('YYYY-MM-DD'),
          endDate: selectedRentalDraft.endDate.format('YYYY-MM-DD'),
        },
      },
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth={false} sx={{ py: 3, px: 2, position: 'relative' }}>
        <Box sx={{ mb: 2, overflowX: 'auto', overflowY: 'visible', pt: 1.25, pb: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap', minWidth: 'max-content' }}>
            <Autocomplete
              freeSolo
              size="small"
              sx={{ minWidth: 240, width: 240, flexShrink: 0 }}
              options={vehicleSearchOptions}
              ListboxProps={{
                sx: {
                  '& .MuiAutocomplete-option': {
                    whiteSpace: 'nowrap',
                  },
                },
              }}
              inputValue={searchQuery}
              onInputChange={(_, value, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  setSearchQuery(value);
                }
              }}
              onChange={(_, value) => {
                if (value && typeof value !== 'string') {
                  setSearchQuery(getVehicleSearchLabel(value));
                }
              }}
              filterOptions={(options, state) => {
                const input = state.inputValue.trim().toLowerCase();
                if (!input) return options;
                return options.filter((option) => getVehicleSearchLabel(option).toLowerCase().includes(input));
              }}
              getOptionLabel={(option) => (typeof option === 'string' ? option : getVehicleSearchLabel(option))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('timeline.searchVehicle')}
                  placeholder={t('timeline.searchVehiclePlaceholder')}
                />
              )}
            />
            <FormControl size="small" sx={{ minWidth: 130, flexShrink: 0 }}>
              <InputLabel>{t('search.vehicleType')}</InputLabel>
              <Select
                value={filterType}
                label={t('search.vehicleType')}
                onChange={(e) => setFilterType(e.target.value as VehicleType | '')}
              >
                <MenuItem value="">{t('search.allTypes')}</MenuItem>
                {Object.values(VehicleType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {vehicleTypeLabel(t, type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150, flexShrink: 0 }}>
              <InputLabel>{t('search.vehicleClass')}</InputLabel>
              <Select
                value={filterClass}
                label={t('search.vehicleClass')}
                onChange={(e) => setFilterClass(e.target.value as VehicleClass | '')}
              >
                <MenuItem value="">{t('search.allClasses')}</MenuItem>
                {Object.values(VehicleClass).map((cls) => (
                  <MenuItem key={cls} value={cls}>
                    {vehicleClassLabel(t, cls)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {hasActiveFilters && (
              <Button size="small" onClick={handleClearFilters} sx={{ flexShrink: 0 }}>
                {t('common.clearFilters')}
              </Button>
            )}
            <DatePicker
              label={t('timeline.startDate')}
              value={viewStart}
              onChange={(d) => d && setViewStart(d.startOf('day'))}
              format={APP_DATE_FORMAT}
              slotProps={{ textField: { size: 'small', sx: { width: 160, flexShrink: 0 } } }}
            />
            <Tooltip title={t('timeline.today')}>
              <IconButton onClick={handleJumpToToday} size="small" sx={{ flexShrink: 0 }}>
                <TodayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                border: 1,
                borderColor: 'divider',
                borderRadius: 999,
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            >
              <Tooltip title={t('timeline.zoomIn')}>
                <span>
                  <IconButton
                    onClick={() => adjustZoom('in')}
                    size="small"
                    disabled={!canZoomIn}
                    aria-label={t('timeline.zoomIn')}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, textAlign: 'center' }}>
                {t('common.daysCount', { count: visibleDays })}
              </Typography>
              <Tooltip title={t('timeline.zoomOut')}>
                <span>
                  <IconButton
                    onClick={() => adjustZoom('out')}
                    size="small"
                    disabled={!canZoomOut}
                    aria-label={t('timeline.zoomOut')}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Tooltip title={t('common.refresh')}>
              <IconButton onClick={handleRefresh} disabled={isLoading} sx={{ flexShrink: 0 }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: showSidePanel
              ? `${SIDEBAR_WIDTH}px 1fr 320px`
              : `${SIDEBAR_WIDTH}px 1fr`,
            height: 'calc(100vh - 220px)',
            minHeight: 400,
            overflow: 'hidden',
            gap: showSidePanel ? 1 : 0,
          }}
        >
          {/* Left column: Vehicle list */}
          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRight: 1,
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                height: ROW_HEIGHT,
                minHeight: ROW_HEIGHT,
                p: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {t('timeline.vehiclesTitle', { count: displayedVehicles.length })}
              </Typography>
            </Box>
            <List dense disablePadding sx={{ overflow: 'auto', flex: 1 }}>
              {displayedVehicles.map((v) => {
                const vehRents = reservationsByVehicle.get(v.id)?.length ?? 0;
                const isSelected = selectedVehicle?.id === v.id;
                const label = v.licensePlate || `${v.make} ${v.model}`;
                return (
                  <ListItem key={v.id} disablePadding sx={{ height: ROW_HEIGHT, minHeight: ROW_HEIGHT }}>
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => {
                        setSelectedVehicle(isSelected ? null : v);
                        setSelectedReservation(null);
                        setPendingSelection(null);
                      }}
                      sx={{
                        height: ROW_HEIGHT,
                        minHeight: ROW_HEIGHT,
                        py: 0,
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Avatar
                        src={v.imageUrls?.[0]}
                        variant="rounded"
                        sx={{
                          width: 36,
                          height: 36,
                          mr: 1.5,
                          flexShrink: 0,
                          bgcolor: 'action.hover',
                        }}
                      >
                        <CarIcon sx={{ color: 'action.active', fontSize: 20 }} />
                      </Avatar>
                      <ListItemText
                        primary={label}
                        secondary={`${v.year} · ${vehRents === 1 ? t('timeline.rentalCount', { count: vehRents }) : t('timeline.rentalsCount', { count: vehRents })}`}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
              {displayedVehicles.length === 0 && !isLoading && (
                <ListItem>
                  <ListItemText
                    primary={hasActiveFilters ? t('timeline.noFilteredVehicles') : t('timeline.noVehicles')}
                    secondary={hasActiveFilters ? t('timeline.noFilteredVehiclesHint') : t('timeline.noVehiclesHint')}
                  />
                </ListItem>
              )}
            </List>
          </Paper>

          {/* Right column: Timeline */}
          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            {/* Timeline header + rows in same scroll container; horizontal panning via drag, vertical via native scroll */}
            <Box
              ref={timelineViewportRef}
              onPointerDown={handleTimelinePointerDown}
              onPointerMove={handleTimelinePointerMove}
              onPointerUp={handleTimelinePointerEnd}
              onPointerCancel={handleTimelinePointerEnd}
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: isDragging ? 'none' : 'auto',
                touchAction: 'pan-y',
              }}
            >
              <Box
                sx={{
                  width: totalCanvasDays * pixelsPerDay,
                  transform: `translateX(${-OVERSCAN_DAYS * pixelsPerDay + dragRemainderPx}px)`,
                  willChange: 'transform',
                }}
              >
                {/* Timeline header */}
                <Box
                  sx={{
                    display: 'flex',
                    height: ROW_HEIGHT,
                    minHeight: ROW_HEIGHT,
                    flexShrink: 0,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                  }}
                >
                  {canvasDays.map((d, i) => {
                    const isTodayDate = d.isSame(today, 'day');
                    return (
                      <Box
                        key={i}
                        sx={{
                          width: pixelsPerDay,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderLeft: 1,
                          borderColor: 'divider',
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          px: 0.5,
                          ...(isTodayDate && {
                            bgcolor: (theme: { palette: { primary: { main: string } } }) =>
                              alpha(theme.palette.primary.main, 0.08),
                            color: 'text.primary',
                          }),
                        }}
                      >
                        {useStackedDayLabel ? (
                          <Box
                            component="span"
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              lineHeight: 1,
                            }}
                          >
                            <Box component="span">{d.format('D')}</Box>
                            <Box component="span">/{d.format('M')}</Box>
                          </Box>
                        ) : (
                          d.format(dayLabelFormat)
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {/* Timeline rows */}
                {displayedVehicles.map((v) => {
                  const vehReservations = reservationsByVehicle.get(v.id) ?? [];
                  const showRow = !selectedVehicle || selectedVehicle.id === v.id;
                  const isDraftVehicle = selectedRentalDraft?.vehicle.id === v.id;
                  const isPendingVehicle = pendingSelection?.vehicleId === v.id;
                  if (!showRow) return null;

                  return (
                    <Box
                      key={v.id}
                      sx={{
                        position: 'relative',
                        height: ROW_HEIGHT,
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          zIndex: 0,
                        }}
                      >
                        {canvasDays.map((day) => {
                          const isAvailable = isVehicleDayAvailable(v.id, day);
                          const isPendingStart = Boolean(
                            isPendingVehicle && pendingSelection?.startDate.isSame(day, 'day')
                          );
                          const isDraftStart = Boolean(
                            isDraftVehicle && selectedRentalDraft?.startDate.isSame(day, 'day')
                          );
                          const isDraftEnd = Boolean(
                            isDraftVehicle && selectedRentalDraft?.endDate.isSame(day, 'day')
                          );
                          const isDraftInRange = Boolean(
                            isDraftVehicle &&
                              selectedRentalDraft &&
                              day.isAfter(selectedRentalDraft.startDate, 'day') &&
                              day.isBefore(selectedRentalDraft.endDate, 'day')
                          );
                          const isTodayDate = day.isSame(today, 'day');

                          return (
                            <Box
                              key={`${v.id}-${day.format('YYYY-MM-DD')}`}
                              component="button"
                              type="button"
                              disabled={!isAvailable}
                              data-role="day-cell"
                              data-vehicle-id={v.id}
                              data-day={day.format('YYYY-MM-DD')}
                              aria-label={t('timeline.selectDateForVehicle', {
                                vehicle: getVehicleLabel(v),
                                date: formatDate(day),
                              })}
                              sx={{
                                width: pixelsPerDay,
                                height: '100%',
                                flexShrink: 0,
                                border: 0,
                                borderLeft: 1,
                                borderColor: 'divider',
                                p: 0,
                                m: 0,
                                bgcolor:
                                  isDraftStart || isDraftEnd
                                    ? 'primary.main'
                                    : isDraftInRange
                                      ? (theme) => alpha(theme.palette.primary.main, 0.14)
                                      : isPendingStart
                                        ? (theme) => alpha(theme.palette.primary.main, 0.2)
                                        : isTodayDate
                                          ? 'action.hover'
                                          : 'transparent',
                                boxShadow:
                                  isPendingStart && !isDraftStart
                                    ? 'inset 0 0 0 1px rgba(25, 118, 210, 0.45)'
                                    : 'none',
                                cursor: isAvailable ? 'pointer' : 'default',
                                opacity: 1,
                                transition: 'background-color 120ms ease, box-shadow 120ms ease',
                                '&:hover': {
                                  bgcolor: isAvailable
                                    ? isDraftStart || isDraftEnd
                                      ? 'primary.dark'
                                      : (theme) => alpha(theme.palette.primary.main, 0.08)
                                    : 'transparent',
                                },
                                '&:disabled': {
                                  cursor: 'default',
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                      {vehReservations.map((res) => {
                        const { left, width } = getReservationPosition(res);
                        const isSelected = selectedReservation?.id === res.id;
                        const driver =
                          res.driver
                            ? `${res.driver.firstName} ${res.driver.lastName}`
                            : res.guestDriverName || t('rental.guest');
                        return (
                          <Tooltip
                            key={res.id}
                            title={
                              <Box>
                                <Typography variant="caption" fontWeight="bold">
                                  {res.reservationNumber}
                                </Typography>
                                <br />
                                {driver} · {formatDate(res.pickupDate || res.startDate)} – {res.returnDate || res.endDate ? formatDate(res.returnDate || res.endDate) : t('rental.returnDateTbd')}
                                <br />
                                <ReservationStatusChip
                                  status={res.status}
                                  sx={{
                                    mt: 0.5,
                                    fontSize: '0.65rem',
                                  }}
                                />
                              </Box>
                            }
                          >
                            <Box
                              data-role="reservation"
                              data-reservation-id={res.id}
                              sx={{
                                position: 'absolute',
                                left: `${left}px`,
                                top: 6,
                                width: `${Math.max(width - 4, 20)}px`,
                                height: ROW_HEIGHT - 12,
                                bgcolor: getReservationStatusColor(res.status),
                                color: 'white',
                                borderRadius: 1,
                                cursor: 'pointer',
                                overflow: 'hidden',
                                border: isSelected ? 2 : 0,
                                borderColor: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                px: 0.5,
                                zIndex: 1,
                                '&:hover': { opacity: 0.9 },
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  fontSize: '0.7rem',
                                }}
                              >
                                {res.reservationNumber} · {driver}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Paper>

          {/* Right side: Selected reservation details */}
          {selectedReservation && (
            <Paper
              sx={{
                overflow: 'auto',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  pr: 0.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {selectedReservation.reservationNumber}
                  </Typography>
                  <ReservationStatusChip
                    status={selectedReservation.status}
                    sx={{ mt: 1 }}
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setSelectedReservation(null)}
                  aria-label={t('common.close')}
                  sx={{ mt: -0.5, mr: -0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>{t('fields.vehicle')}</Typography>
                <Typography variant="body2" gutterBottom>
                  {selectedReservation.vehicle
                    ? `${selectedReservation.vehicle.make} ${selectedReservation.vehicle.model} (${selectedReservation.vehicle.year})`
                    : '-'}
                </Typography>
                {selectedReservation.vehicle?.licensePlate && (
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {selectedReservation.vehicle.licensePlate}
                  </Typography>
                )}
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>{t('fields.driver', 'Driver')}</Typography>
                <Typography variant="body2" gutterBottom>
                  {selectedReservation.driver
                    ? `${selectedReservation.driver.firstName} ${selectedReservation.driver.lastName}`
                    : selectedReservation.guestDriverName || t('rental.guest')}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('timeline.period')}
                </Typography>
                <Typography variant="body2">
                  {formatDate(selectedReservation.pickupDate || selectedReservation.startDate)} – {selectedReservation.returnDate || selectedReservation.endDate ? formatDate(selectedReservation.returnDate || selectedReservation.endDate) : t('rental.returnDateTbd')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('common.daysCount', { count: selectedReservation.totalDays })}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('fields.pickupReturn')}
                </Typography>
                <Typography variant="body2">
                  {selectedReservation.pickupLocation} → {selectedReservation.returnLocation}
                </Typography>
              </CardContent>
            </Paper>
          )}

          {!selectedReservation && selectedRentalDraft && (
            <Paper
              sx={{
                overflow: 'auto',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  pr: 0.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('timeline.draftTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('timeline.draftReady')}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedRentalDraft(null);
                    setPendingSelection(null);
                  }}
                  aria-label={t('common.close')}
                  sx={{ mt: -0.5, mr: -0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('fields.vehicle')}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  {`${selectedRentalDraft.vehicle.make} ${selectedRentalDraft.vehicle.model} (${selectedRentalDraft.vehicle.year})`}
                </Typography>
                {selectedRentalDraft.vehicle.licensePlate && (
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {selectedRentalDraft.vehicle.licensePlate}
                  </Typography>
                )}
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('timeline.period')}
                </Typography>
                <Typography variant="body2">
                  {formatDate(selectedRentalDraft.startDate)} - {formatDate(selectedRentalDraft.endDate)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('common.daysCount', { count: selectedRangeDays })}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Button variant="contained" fullWidth onClick={handleCreateRental}>
                  {t('walkIn.createRental')}
                </Button>
              </CardContent>
            </Paper>
          )}
        </Box>

        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          />
        )}
      </Container>
    </LocalizationProvider>
  );
};
